import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Package, ChevronDown, ChevronRight, Plus, X } from "lucide-react";
import { getIcon } from "@/lib/iconRegistry";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface FieldOption {
  key: string;
  label: string;
}

interface ImageSlot {
  tag: string;
  label: string;
  description: string;
}

interface FormField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  unit?: string;
  options?: FieldOption[];
  slots?: ImageSlot[];
}

interface FormSection {
  id: string;
  title: string;
  icon?: string;
  description?: string;
  fields: FormField[];
}

interface ProductFormConfig {
  version?: string;
  productLabel?: string;
  sections: FormSection[];
}

interface SectionProps {
  icon: React.ElementType;
  title: string;
  description?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Section({ icon: Icon, title, description, children, defaultOpen = true }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
      >
        <Icon className="w-4 h-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold">{title}</span>
          {description && !open && (
            <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">{description}</span>
          )}
        </div>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {description && (
              <p className="text-xs text-muted-foreground px-4 pt-3 pb-0">{description}</p>
            )}
            <div className="p-4 space-y-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TagListInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [inputVal, setInputVal] = useState("");

  const addTag = () => {
    const trimmed = inputVal.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputVal("");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder={placeholder}
          className="text-sm flex-1"
        />
        <Button type="button" size="sm" variant="outline" onClick={addTag} disabled={!inputVal.trim()}>
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((item, i) => (
            <Badge key={i} variant="secondary" className="text-xs font-normal gap-1 pr-1">
              <span className="truncate max-w-[200px]">{item}</span>
              <button
                onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                className="ml-0.5 hover:text-destructive transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}


export function ProductFormPreview({ config, values, onChange }: { 
  config: ProductFormConfig;
  values?: Record<string, any>;
  onChange?: (values: Record<string, any>) => void;
}) {
  const [internalValues, setInternalValues] = useState<Record<string, any>>(values || {});

  const formValues = values !== undefined ? values : internalValues;

  const setValue = (key: string, value: any) => {
    if (onChange) {
      onChange({ ...formValues, [key]: value });
    } else {
      setInternalValues((prev) => ({ ...prev, [key]: value }));
    }
  };

  const getValue = (key: string): any => formValues[key] ?? "";

  const renderField = (field: FormField) => {
    switch (field.type) {
      case "text":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label className="text-xs font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                value={getValue(field.key)}
                onChange={(e) => setValue(field.key, e.target.value)}
                placeholder={field.placeholder}
                className="text-sm"
              />
              {field.unit && (
                <span className="text-xs text-muted-foreground shrink-0">{field.unit}</span>
              )}
            </div>
          </div>
        );

      case "number":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label className="text-xs font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={getValue(field.key)}
                onChange={(e) => setValue(field.key, e.target.value)}
                placeholder={field.placeholder}
                className="text-sm w-32"
              />
              {field.unit && (
                <span className="text-xs text-muted-foreground shrink-0">{field.unit}</span>
              )}
            </div>
          </div>
        );

      case "textarea":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label className="text-xs font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <Textarea
              value={getValue(field.key)}
              onChange={(e) => setValue(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="text-sm min-h-[80px] resize-y"
            />
          </div>
        );

      case "select":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label className="text-xs font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <Select
              value={getValue(field.key) || undefined}
              onValueChange={(v) => setValue(field.key, v)}
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map((opt) => (
                  <SelectItem key={opt.key} value={opt.key}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "tag_list":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label className="text-xs font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <TagListInput
              value={Array.isArray(getValue(field.key)) ? getValue(field.key) : []}
              onChange={(v) => setValue(field.key, v)}
              placeholder={field.placeholder}
            />
          </div>
        );

      case "image_slot":
        // Image slots handled in dedicated Images tab
        return null;

      default:
        return (
          <div key={field.key} className="text-xs text-muted-foreground italic">
            Unknown field type: {field.type}
          </div>
        );
    }
  };

  const renderSection = (section: FormSection) => {
    // Skip sections that only contain image_slot fields (handled in Images tab)
    const nonImageFields = section.fields.filter((f) => f.type !== "image_slot");
    if (nonImageFields.length === 0) return null;

    const Icon = getIcon(section.icon || "package") || Package;

    // Group number fields in a row for compact layout
    const numberFields = section.fields.filter((f) => f.type === "number");
    const otherFields = section.fields.filter((f) => f.type !== "number");
    const hasNumbers = numberFields.length > 1;

    return (
      <Section
        key={section.id}
        icon={Icon}
        title={section.title}
        description={section.description}
      >
        {hasNumbers && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {numberFields.map((f) => renderField(f))}
          </div>
        )}
        {(hasNumbers ? otherFields : section.fields).map((f) =>
          !hasNumbers || f.type !== "number" ? renderField(f) : null
        )}
      </Section>
    );
  };

  // Count filled fields
  const totalFields = config.sections.flatMap((s) => s.fields).filter((f) => f.type !== "image_slot").length;
  const filledFields = config.sections
    .flatMap((s) => s.fields)
    .filter((f) => {
      if (f.type === "image_slot") return false;
      const val = getValue(f.key);
      if (Array.isArray(val)) return val.length > 0;
      return !!val;
    }).length;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card shrink-0">
        <Package className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">{config.productLabel || "Product Form"}</span>
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground">{filledFields}/{totalFields} fields</span>
      </div>

      {/* Form sections */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {config.sections.map((section) => renderSection(section))}
      </div>
    </div>
  );
}
